import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, ref, type InferType } from "yup";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, X } from "lucide-react";
import { Alert, AlertDescription, Button, Input, PasswordInput } from "@envirohub/ui";
import { auth, clearPasswordInputs } from "@envirohub/utils";
import { logoutServerSession, useAcceptInvitation } from "@/api";
import { useAuthError } from "@/hooks/useAuthError";
import {
  readInvitationToken,
  resolveAcceptInvitationError,
  type AcceptInvitationOutcome,
  type InvitationLoginState,
} from "@/lib/invitations";

const schema = object({
  firstName: string().trim().required("validation.firstNameMin").min(2, "validation.firstNameMin"),
  lastName: string().trim().required("validation.lastNameMin").min(2, "validation.lastNameMin"),
  password: string()
    .required("validation.passwordRequired")
    .min(8, "validation.passwordRequirements")
    .matches(/[A-Z]/, "validation.passwordRequirements")
    .matches(/[a-z]/, "validation.passwordRequirements")
    .matches(/[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, "validation.passwordRequirements"),
  confirmPassword: string()
    .oneOf([ref("password")], "validation.passwordsMatch")
    .required("validation.passwordsMatch"),
});

type FormData = InferType<typeof schema>;

const FORM_FIELDS = ["firstName", "lastName", "password", "confirmPassword"] as const;

type TerminalOutcome = Exclude<AcceptInvitationOutcome, "fields" | "generic">;

const TERMINAL_TITLE_KEYS: Record<TerminalOutcome, string> = {
  invalid: "auth.inviteAccept.invalidTitle",
  rolesInvalid: "auth.inviteAccept.rolesInvalidTitle",
  emailExists: "auth.inviteAccept.emailExistsTitle",
};

export default function InviteAcceptPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = readInvitationToken(searchParams);
  const resolveAuthError = useAuthError();
  const accept = useAcceptInvitation();

  const [hasSession, setHasSession] = useState(() => Boolean(auth.getSession()));
  const [serverError, setServerError] = useState<string | null>(null);
  const [terminal, setTerminal] = useState<{ outcome: TerminalOutcome; message: string } | null>(
    null,
  );

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  const passwordRequirementLabels = {
    length: t("validation.passwordLength"),
    upper: t("validation.passwordUpper"),
    lower: t("validation.passwordLower"),
    numberOrSpecial: t("validation.passwordNumberOrSpecial"),
  };

  const handleSignOut = () => {
    logoutServerSession().catch(() => {});
    auth.clearAuth();
    setHasSession(false);
  };

  const onSubmit = (data: FormData) => {
    if (!token) return;
    setServerError(null);
    accept.mutate(
      { token, firstName: data.firstName, lastName: data.lastName, password: data.password },
      {
        onSuccess: (result) => {
          clearPasswordInputs();
          const state: InvitationLoginState = { email: result.email, invitationAccepted: true };
          navigate("/login", { replace: true, state });
        },
        onError: (error) => {
          const action = resolveAuthError(error);
          const outcome = resolveAcceptInvitationError(action, FORM_FIELDS);
          if (outcome === "fields") {
            Object.entries(action.fieldErrors).forEach(([key, message]) => {
              if ((FORM_FIELDS as readonly string[]).includes(key)) {
                setError(key as keyof FormData, { message });
              }
            });
            return;
          }
          if (outcome === "generic") {
            setServerError(action.message);
            return;
          }
          setTerminal({ outcome, message: action.message });
        },
      },
    );
  };

  if (!token || terminal) {
    const outcome: TerminalOutcome = terminal?.outcome ?? "invalid";
    const description =
      outcome === "emailExists"
        ? t("auth.inviteAccept.emailExistsDescription")
        : (terminal?.message ?? t("errors.codes.INVITATION_INVALID"));

    return (
      <div className="flex flex-col gap-6 text-center">
        <div>
          <h1 className="text-h3 font-bold text-surface-on dark:text-white">
            {t(TERMINAL_TITLE_KEYS[outcome])}
          </h1>
          <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-2">
            {description}
          </p>
          {outcome !== "emailExists" && (
            <p className="text-body-sm text-surface-on-variant dark:text-[#C3C9B4] mt-2">
              {t("auth.inviteAccept.invalidHint")}
            </p>
          )}
        </div>
        <Link to="/login" className="text-primary font-bold no-underline hover:underline">
          {outcome === "emailExists" ? t("auth.login") : t("auth.backToLogin")}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <h1 className="text-h3 font-bold text-surface-on dark:text-white">
          {t("auth.inviteAccept.title")}
        </h1>
        <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-1">
          {t("auth.inviteAccept.subtitle")}
        </p>
      </div>

      {hasSession ? (
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-3">
            <span>{t("auth.inviteAccept.signedInWarning")}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={handleSignOut}
            >
              {t("auth.logout")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          {serverError && (
            <Alert variant="destructive" className="ps-10">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{serverError}</AlertDescription>
              <button
                type="button"
                onClick={() => setServerError(null)}
                className="absolute end-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </Alert>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              id="firstName"
              type="text"
              autoComplete="given-name"
              label={t("auth.firstName")}
              placeholder={t("auth.firstNamePlaceholder")}
              error={errors.firstName?.message ? t(errors.firstName?.message) : undefined}
              required
              {...register("firstName")}
            />
            <Input
              id="lastName"
              type="text"
              autoComplete="family-name"
              label={t("auth.lastName")}
              placeholder={t("auth.lastNamePlaceholder")}
              error={errors.lastName?.message ? t(errors.lastName?.message) : undefined}
              required
              {...register("lastName")}
            />
          </div>

          <PasswordInput
            id="password"
            autoComplete="new-password"
            label={t("auth.password")}
            placeholder={t("auth.passwordPlaceholder")}
            error={errors.password?.message ? t(errors.password?.message) : undefined}
            showStrengthRequirements
            requirementLabels={passwordRequirementLabels}
            required
            {...register("password")}
          />

          <PasswordInput
            id="confirmPassword"
            autoComplete="new-password"
            label={t("auth.confirmPassword")}
            placeholder={t("auth.confirmPasswordPlaceholder")}
            error={errors.confirmPassword?.message ? t(errors.confirmPassword?.message) : undefined}
            required
            {...register("confirmPassword")}
          />

          <Button
            type="submit"
            variant="default"
            size="lg"
            loading={accept.isPending}
            className="w-full mt-2 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
          >
            {t("auth.inviteAccept.submit")}
          </Button>
        </form>
      )}
    </div>
  );
}
